'use client';

import { useState } from 'react';
import { ShoppingCart, Check, Loader2 } from 'lucide-react';
import { useCartStore } from '@/stores/cart';
import { useMiniCartUI } from '@/stores/mini-cart-ui';
import { translateProductName } from '@/lib/product-translations';
import { gtagEvent } from '@/components/GoogleAnalytics';

interface QuickAddProduct {
  id: number;
  reference: string;
  name: string;
  price: number;
  quantity?: number;
  allow_backorder?: boolean;
  manufacturer_name?: string;
  category_name?: string;
  images?: Array<{ id?: number; external_url: string; position?: number; cover?: boolean }>;
}

interface Props {
  product: QuickAddProduct;
  /** Compact = icon-only round button (for tight grid cells). Default = labeled. */
  compact?: boolean;
}

/**
 * One-tap add-to-cart button for product grids. Doesn't navigate — adds the
 * item and shows a toast so the customer can keep browsing the grid.
 */
export function QuickAddButton({ product, compact }: Props) {
  const addItem = useCartStore((s) => s.addItem);
  const [added, setAdded] = useState(false);
  const [loading, setLoading] = useState(false);

  const inStock = (product.quantity ?? 0) > 0 || product.allow_backorder === true;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!inStock || loading) return;
    setLoading(true);
    try {
      const cartProduct = {
        id: product.id,
        reference: product.reference,
        name: translateProductName(product.name),
        price: product.price,
        quantity: product.quantity ?? 0,
        active: true,
        allow_backorder: product.allow_backorder,
        manufacturer_name: product.manufacturer_name,
        category_name: product.category_name,
        images: (product.images || []).map((img, i) => ({
          id: img.id ?? i,
          external_url: img.external_url,
          position: img.position ?? i,
          cover: img.cover ?? i === 0,
        })),
      };
      // The cart store expects a full Product; we cast since we provide all
      // the fields the cart actually reads.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      addItem(cartProduct as any, 1);
      setAdded(true);
      // Mini-cart slideout shows added product + total — replaces the toast.
      useMiniCartUI.getState().open();
      try {
        gtagEvent('add_to_cart', {
          currency: 'SEK',
          value: product.price,
          items: [{ item_id: product.reference, item_name: product.name, price: product.price, quantity: 1 }],
        });
      } catch {
        /* ignore */
      }
      setTimeout(() => setAdded(false), 2000);
    } finally {
      setLoading(false);
    }
  };

  if (!inStock) {
    return null;
  }

  if (compact) {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-label="Lägg i varukorg"
        className={`inline-flex items-center justify-center h-9 w-9 rounded-full text-white shadow-md hover:shadow-lg transition-all active:scale-95 ${
          added ? 'bg-green-600' : 'bg-[#0055A4] hover:bg-[#0f2e5a]'
        }`}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : added ? <Check className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`w-full mt-2 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition-all active:scale-[0.98] ${
        added ? 'bg-green-600' : 'bg-[#0055A4] hover:bg-[#0f2e5a]'
      }`}
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Lägger till…
        </>
      ) : added ? (
        <>
          <Check className="h-4 w-4" />
          Tillagd
        </>
      ) : (
        <>
          <ShoppingCart className="h-4 w-4" />
          Köp 1 st
        </>
      )}
    </button>
  );
}
